import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import permissionService from '../../services/permissionService';
import './Permissions.css';

const PermissionsManagement = () => {
  const { user } = useContext(AuthContext);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    resource: '',
    action: '',
    description: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 5,
    totalPages: 0
  });

  // Helper function to check if user has permission
  const hasPermission = (permission) => {
    if (user?.roles?.some(r => ['admin', 'manager'].includes(r.name))) {
      return true;
    }
    return user?.permissions?.some(p => p.name === permission);
  };

  useEffect(() => {
    fetchPermissions();
  }, [currentPage, searchTerm]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionService.getAllPermissions({
        page: currentPage,
        limit: 5,
        search: searchTerm
      });

      if (response.success) {
        setPermissions(response.data?.permissions || []);
        setPagination(response.data?.pagination || { total: 0, page: 1, limit: 5, totalPages: 0 });
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateNew = () => {
    setEditingPermission(null);
    setFormData({
      name: '',
      resource: '',
      action: '',
      description: ''
    });
    setShowModal(true);
  };

  const handleEdit = (permissionToEdit) => {
    setEditingPermission(permissionToEdit);
    setFormData({
      name: permissionToEdit.name,
      resource: permissionToEdit.resource || '',
      action: permissionToEdit.action || '',
      description: permissionToEdit.description || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      let response;
      if (editingPermission) {
        response = await permissionService.updatePermission(editingPermission.id, formData);
      } else {
        response = await permissionService.createPermission(formData);
      }

      if (response.success) {
        setSuccessMessage(editingPermission ? 'Permission updated successfully!' : 'Permission created successfully!');
        setShowModal(false);
        setEditingPermission(null);
        resetForm();
        fetchPermissions();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error(editingPermission ? 'Update permission error:' : 'Create permission error:', err);
      let errorMessage = editingPermission ? 'Failed to update permission' : 'Failed to create permission';
      if (err.errors && Array.isArray(err.errors)) {
        errorMessage = err.errors.map(e => e.msg).join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    }
  };

  const handleDelete = async (permissionToDelete) => {
    if (!window.confirm(`Are you sure you want to delete the permission "${permissionToDelete.name}"?`)) {
      return;
    }

    try {
      const response = await permissionService.deletePermission(permissionToDelete.id);
      if (response.success) {
        setSuccessMessage('Permission deleted successfully!');
        fetchPermissions();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Delete permission error:', err);
      setError(err.message || 'Failed to delete permission');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      resource: '',
      action: '',
      description: ''
    });
  };

  if (!hasPermission('permissions.read')) {
    return (
      <div className="permissions-management">
        <div className="section-header">
          <h2>Access Denied</h2>
        </div>
        <div className="error-message">
          You need the 'permissions.read' permission to access Permission Management.
        </div>
      </div>
    );
  }

  return (
    <div className="permissions-management">
      <div className="section-header">
        <div>
          <h2>Permission Management</h2>
          <p>Manage system permissions and access controls</p>
        </div>
        {hasPermission('permissions.create') && (
          <button className="btn-create" onClick={handleCreateNew}>
            + Create Permission
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search by name, resource, action, or description..."
          value={searchTerm}
          onChange={handleSearch}
        />
        <span className="search-icon">🔍</span>
      </div>

      {loading ? (
        <div className="loading">Loading permissions...</div>
      ) : (
        <>
          <div className="permissions-table-container">
            <table className="permissions-table">
              <thead>
                <tr>
                  <th>Permission Name</th>
                  <th>Resource</th>
                  <th>Action</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {permissions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-data">No permissions found</td>
                  </tr>
                ) : (
                  permissions.map(permission => (
                    <tr key={permission.id}>
                      <td><strong>{permission.name}</strong></td>
                      <td>
                        <span className="resource-badge">
                          {permission.resource || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className="action-badge">
                          {permission.action || 'N/A'}
                        </span>
                      </td>
                      <td>{permission.description || 'No description'}</td>
                      <td>
                        <div className="action-buttons">
                          {hasPermission('permissions.update') && (
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(permission)}
                              title="Edit Permission"
                            >
                              ✏️
                            </button>
                          )}
                          {hasPermission('permissions.delete') && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(permission)}
                              title="Delete Permission"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {permissions.length} of {pagination.total} permissions
                {searchTerm && ` (filtered by "${searchTerm}")`}
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  ◀ Previous
                </button>
                <span className="page-indicator">
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === pagination.totalPages}
                >
                  Next ▶
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Permission Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPermission ? 'Edit Permission' : 'Create New Permission'}</h3>
              <button className="close-button" onClick={() => { setShowModal(false); setEditingPermission(null); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Permission Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., users.create, roles.read, etc."
                  />
                </div>

                <div className="form-group">
                  <label>Resource *</label>
                  <input
                    type="text"
                    name="resource"
                    value={formData.resource}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., users, roles, etc."
                  />
                </div>

                <div className="form-group">
                  <label>Action *</label>
                  <input
                    type="text"
                    name="action"
                    value={formData.action}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., create, read, update, delete"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Describe what this permission allows..."
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); setEditingPermission(null); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingPermission ? 'Update Permission' : 'Create Permission'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionsManagement;
